import { createTheme } from '@mui/material';

import {
  getAutocompleteStyles,
  getAutocompletePaperStyles,
} from './autocomplete/autocomplete.styles';
import { BUTTON_SIZES, BUTTON_HIERARCHY_STYLES } from './button/button.styles';
import { isValidButtonSize } from './button/button.types';
import { CHIP_STYLES } from './chip/chip.styles';
import { isValidChipSize, isValidChipType, isValidChipVariant } from './chip/chip.types';
import { COLOR_TOKENS } from './colors/colors';
import {
  getFormGroupStyles,
  getFormControlLabelStyles,
  getRadioStyles,
  getCheckboxStyles,
} from './form/form.styles';
import {
  getSelectMenuPaperStyles,
  getMenuItemStyles,
  getSelectStyles,
} from './select/select.styles';
import { SHADOWS } from './shadows/shadows';
import { getSnackbarStyles } from './snackbar/snackbar.styles';
import { CONTAINER_SPACING, RADIUS, SPACING, WIDTH } from './spacing/spacing';
import {
  getFormHelperTextStyles,
  getFormLabelAsteriskStyles,
  getFormLabelStyles,
  getOutlinedInputInputStyles,
  getOutlinedInputNotchedOutlineStyles,
  getOutlinedInputRootStyles,
} from './textField/textField.styles';
import { getToolTipStyles, getToolTipArrowStyles } from './tooltip/tooltip.styles';
import { TYPOGRAPHY_VARIANTS, TYPOGRAPHY_VARIANTS_MAPPING } from './typography/typography';

export const theme = createTheme({
  // element variants, such as Typography or Button
  typography: { ...TYPOGRAPHY_VARIANTS },

  // custom theme properties/design system tokens
  backrs: {
    colors: COLOR_TOKENS,
    spacing: SPACING,
    radius: RADIUS,
    width: WIDTH,
    containerSpacing: CONTAINER_SPACING,
    shadows: SHADOWS,
  },

  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"Sarabun", sans-serif',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          fontFamily: '"Sarabun", sans-serif',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: `${SPACING['spacing-none']} !important`,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => getFormHelperTextStyles(theme),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => getFormLabelStyles(theme),
        asterisk: ({ theme }) => getFormLabelAsteriskStyles(theme),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => getOutlinedInputRootStyles(theme),
        notchedOutline: () => getOutlinedInputNotchedOutlineStyles(),
        input: ({ theme }) => getOutlinedInputInputStyles(theme),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => getToolTipStyles(theme),
        arrow: ({ theme }) => getToolTipArrowStyles(theme),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState: { size = 'md', type = 'default', variant = 'badge' } }) => {
          if (!isValidChipType(type) || !isValidChipSize(size) || !isValidChipVariant(variant)) {
            return {};
          }

          return {
            ...CHIP_STYLES[size][type][variant],
          };
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState: { hierarchy, size, icon = 'Default' } }) => {
          if (!hierarchy || !isValidButtonSize(size)) {
            // if the button being rendered doesn't include a hierarchy or valid size, return empty object to avoid mutating existing styles
            return { boxShadow: 'none' };
          }

          return {
            ...BUTTON_SIZES[size][icon],
            ...BUTTON_HIERARCHY_STYLES[hierarchy],
          };
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => getSelectStyles(theme),
      },
      defaultProps: {
        MenuProps: {
          PaperProps: {
            sx: (theme) => getSelectMenuPaperStyles(theme),
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => theme.unstable_sx(getSelectMenuPaperStyles(theme)),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => getMenuItemStyles(theme),
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: ({ theme }) => getFormGroupStyles(theme),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme, ownerState }) => getFormControlLabelStyles(theme, ownerState.checked),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme, ownerState }) => getRadioStyles(theme, ownerState.checked),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => getCheckboxStyles(theme),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: TYPOGRAPHY_VARIANTS_MAPPING,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: ({ theme }) => getAutocompleteStyles(theme),
        paper: ({ theme }) => getAutocompletePaperStyles(theme),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => getSnackbarStyles(theme),
      },
    },
  },
});
